import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { authService, LoginData, RegisterData } from '@/services/auth.service';
import { useAuthStore } from '@/stores/auth';
import { scheduleTokenRefresh, clearTokenRefresh } from '@/services/api';
import {
  communityService,
  type CommunityStatus,
} from '@/services/community.service';
import { endSession } from '@/components/community/client';

export const authKeys = {
  all: ['auth'] as const,
  profile: () => [...authKeys.all, 'profile'] as const,
};

export function useProfile() {
  const { isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: authKeys.profile(),
    queryFn: () => authService.getProfile(),
    enabled: isAuthenticated,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  const { login } = useAuthStore();

  return useMutation({
    mutationFn: (data: LoginData) => authService.login(data),
    onSuccess: (response) => {
      login(response.user, response.tokens);
      scheduleTokenRefresh(response.tokens.expiresIn || 432000);
      queryClient.invalidateQueries({ queryKey: authKeys.profile() });
    },
  });
}

export function useRegister() {
  const queryClient = useQueryClient();
  const { login } = useAuthStore();

  return useMutation({
    mutationFn: (data: RegisterData) => authService.register(data),
    onSuccess: (response) => {
      login(response.user, response.tokens);
      scheduleTokenRefresh(response.tokens.expiresIn || 432000);
      queryClient.invalidateQueries({ queryKey: authKeys.profile() });
    },
  });
}

async function endCommunitySession(queryClient: QueryClient) {
  try {
    const status =
      queryClient.getQueryData<CommunityStatus>(['community', 'status']) ??
      (await communityService.getStatus());
    if (status?.enabled && status.apiUrl) {
      await endSession(status.apiUrl);
    }
  } catch {
    return;
  }
}

export function useLogout() {
  const queryClient = useQueryClient();
  const { logout } = useAuthStore();

  return useMutation({
    mutationFn: async () => {
      await endCommunitySession(queryClient);
      await authService.logout();
    },
    onSuccess: () => {
      clearTokenRefresh();
      logout();
      queryClient.clear();
    },
    onError: () => {
      clearTokenRefresh();
      logout();
      queryClient.clear();
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { setUser } = useAuthStore();

  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      authService.updateProfile(data),
    onSuccess: (user) => {
      setUser(user as Parameters<typeof setUser>[0]);
      queryClient.invalidateQueries({ queryKey: authKeys.profile() });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: ({
      currentPassword,
      newPassword,
    }: {
      currentPassword: string;
      newPassword: string;
    }) => authService.changePassword(currentPassword, newPassword),
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => authService.forgotPassword(email),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: ({ token, password }: { token: string; password: string }) =>
      authService.resetPassword(token, password),
  });
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: (token: string) => authService.verifyEmail(token),
  });
}

export function useResendVerificationEmail() {
  return useMutation({
    mutationFn: () => authService.resendVerificationEmail(),
  });
}

// 2FA Hooks
export function useSetup2FA() {
  return useMutation({
    mutationFn: () => authService.setup2FA(),
  });
}

export function useEnable2FA() {
  const { setUser } = useAuthStore();

  return useMutation({
    mutationFn: (code: string) => authService.enable2FA(code),
    onSuccess: () => {
      // Refresh user data after enabling 2FA
      authService.getProfile().then(setUser);
    },
  });
}

export function useDisable2FA() {
  const { setUser } = useAuthStore();

  return useMutation({
    mutationFn: (code: string) => authService.disable2FA(code),
    onSuccess: () => {
      // Refresh user data after disabling 2FA
      authService.getProfile().then(setUser);
    },
  });
}

export function useVerify2FA() {
  const queryClient = useQueryClient();
  const { login } = useAuthStore();

  return useMutation({
    mutationFn: (code: string) => authService.verify2FA(code),
    onSuccess: (response) => {
      login(response.user, response.tokens);
      scheduleTokenRefresh(response.tokens.expiresIn || 432000);
      queryClient.invalidateQueries({ queryKey: authKeys.profile() });
    },
  });
}
